import useTranslations from 'hooks/useTranslations'
import { useDisplayVariant } from 'modules/productDetail'
import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import PriceAtom, * as Prices from 'theme/atoms/Price'
import DeliveryBadge from './DeliveryBadge/DeliveryBadge'
import { useCustomTailor } from 'modules/productDetail'

type Props = {
  amount: number
  logic: 'piece' | 'pack'
  hideBadges: boolean
  'data-cy-state'?: string
  isBulky?: boolean
  showDeliveryTime?: boolean
}

export default function Price(props: Props) {
  const variant = useDisplayVariant()
  const customTailor = useCustomTailor()
  const t = useTranslations<'templates-PDP'>()
  const [prices, setPrices] = React.useState(variant.data.prices)

  const isCustomTailorConfigured =
    customTailor.data.brink && customTailor.data.shape && customTailor.data.size

  const isCustomTailor =
    variant.data.configurations.custom_tailor.is_custom_tailor

  /*
    * If custom tailor is configured,
      we toggle the price to the custom tailor price (including pseudo price)
  */

  React.useEffect(() => {
    if (isCustomTailorConfigured) {
      setPrices({
        ...prices,
        packPriceNet: customTailor.getSinglePrice(
          variant.data.prices.packPriceNet,
          variant
        ),
        packPriceGross: customTailor.getSinglePrice(
          variant.data.prices.packPriceGross,
          variant
        ),
        packPseudoPriceNet: customTailor.getSinglePrice(
          variant.data.prices.packPseudoPriceNet,
          variant
        ),
        packPseudoPriceGross: customTailor.getSinglePrice(
          variant.data.prices.packPseudoPriceGross,
          variant
        ),
        piecePriceNet: customTailor.getSinglePrice(
          variant.data.prices.piecePriceNet,
          variant
        ),
        piecePriceGross: customTailor.getSinglePrice(
          variant.data.prices.piecePriceGross,
          variant
        ),
        piecePseudoPriceNet: customTailor.getSinglePrice(
          variant.data.prices.piecePseudoPriceNet,
          variant
        ),
        piecePseudoPriceGross: customTailor.getSinglePrice(
          variant.data.prices.piecePseudoPriceGross,
          variant
        )
      })
    } else {
      setPrices(variant.data.prices)
    }
  }, [customTailor.data, variant.data.prices])

  const packAmount = 1

  const variantHasStrikePrice = Prices.PriceHasStrikePrice(
    variant.data,
    props.amount
  )

  const showBasePrice = Boolean(
    variant.data.prices.referencePriceNetString ||
      variant.data.prices.referencePriceGrossString
  )

  return (
    <Wrapper
      className="Price"
      data-cy-collection="Price"
      data-cy-state={props['data-cy-state']}
      $hideBadges={props.hideBadges}
    >
      <div className="price">
        <div className="price-list">
          <div
            className={
              variantHasStrikePrice
                ? 'display-price-big reduced'
                : 'display-price-big'
            }
          >
            <PriceAtom
              prices={prices}
              logic={props.logic}
              amount={packAmount}
              scalePrice={variant.data.priceRules}
            />
          </div>

          {variantHasStrikePrice && (
            <>
              <div
                className="strike price"
                data-cy-state="has-strike-price:piece"
              >
                <Prices.PriceStrike
                  prices={prices}
                  logic={props.logic}
                  amount={packAmount}
                  scalePrice={variant.data.priceRules}
                />
              </div>
              <div
                className="reduction"
                data-cy-state="has-strike-price:piece-reduction"
              >
                <Prices.PriceReduction
                  prices={prices}
                  logic={props.logic}
                  amount={packAmount}
                  scalePrice={variant.data.priceRules}
                />
              </div>
            </>
          )}
        </div>
        <div className="additional-info-text-m">
          <div className="additional-info-text-s">
            <div className="unit text">
              {isCustomTailor && !isCustomTailorConfigured ? (
                <span data-cy-state="squaremeter-text">
                  {` ${t.asText(false, 'squaremeter_price')} `}
                </span>
              ) : (
                <span className="piece-price-subtext">
                  {props.logic === 'piece' && (
                    <span data-cy-state="show-piece-price-subtext">
                      {t('per_piece_text')}
                      &#160;
                      {variant.data.unit.unitName}
                      {variant.data.unit.purchaseUnit === 1 && (
                        <>
                          ,&#160;
                          <Prices.PriceB2BGrossInfo
                            prices={prices}
                            scalePrice={variant.data.priceRules}
                            amount={packAmount}
                            logic={props.logic}
                          />
                          <Prices.PriceB2CNetInfo
                            prices={prices}
                            scalePrice={variant.data.priceRules}
                            amount={packAmount}
                            logic={props.logic}
                          />
                        </>
                      )}
                    </span>
                  )}
                  {props.logic === 'pack' && (
                    <span data-cy-state="show-pack-price-subtext">
                      {variant.data.unit.purchaseUnit}
                      &#160;
                      {variant.data.unit.unitName}
                      &#160;/&#160;
                      {variant.data.unit.packUnit}
                      ,&#160;
                      <Prices.PriceB2BGrossInfo
                        prices={prices}
                        scalePrice={variant.data.priceRules}
                        amount={packAmount}
                        logic={props.logic}
                      />
                      <Prices.PriceB2CNetInfo
                        prices={prices}
                        scalePrice={variant.data.priceRules}
                        amount={packAmount}
                        logic={props.logic}
                      />
                    </span>
                  )}
                </span>
              )}

              {showBasePrice && props.logic === 'piece' && (
                <span data-cy-state="show-baseprice">
                  {'; '}
                  <Prices.PriceBaseprice
                    prices={prices}
                    logic={props.logic}
                    data-cy-state="has-baseprice"
                  />
                </span>
              )}
            </div>
            {(variant.data.prices.ecoTaxNet ||
              variant.data.prices.ecoTaxGross) && (
              <Prices.EcoTax
                prices={prices}
                data-cy-state="eco-tax"
                className="additional-info-ecoTax"
              />
            )}
          </div>
          {props.hideBadges == false ? (
            <DeliveryBadge
              amount={props.amount}
              data-cy-state="show-delivery-badges"
              isBulky={props.isBulky}
              showDeliveryTime={props.showDeliveryTime}
            />
          ) : (
            <></>
          )}
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $hideBadges: boolean }>`
  display: flex;

  > .price {
    flex: 1;
    > .price-list {
      display: flex;
      > * {
        margin-left: ${theme.spacing('s')};
        &:first-child {
          margin-left: 0;
        }
      }
      align-items: flex-end;
      > .display-price-big {
        color: ${theme.colors.b0};
        ${theme.ty('rc-2xl', '700')};

        &.reduced {
          color: ${theme.colors.accent.red};
        }
      }
      > .strike {
        ${theme.ty('r-base')}
      }
      > .reduction {
        color: ${theme.colors.accent.red};
        ${theme._ty([16, 0, 22], theme.fontSpecial, '700')}
      }
      /* set every price to same baseline */
      line-height: 1.625rem;
      align-items: baseline;
    }
    .croatiaCurrency {
      width: 4.25rem;
      height: 1.125rem;
      font-family: 'Roboto';
      font-style: normal;
      font-weight: 700;
      font-size: 0.8125rem;
      line-height: 1.125rem;
      color: #625d52;
      flex: none;
      order: 0;
      flex-grow: 0;
    }
    .additional-info-text-m {
      display: ${(p) => (p.$hideBadges ? 'block' : 'flex')};
      justify-content: space-between;
      column-gap: ${theme.spacing('s')};
      ${theme.ty('r-xs', '400', 'r-s')}
      min-height: 2.8125rem;
      > div {
        flex-basis: 50%;
      }
      > .additional-info-text-s {
        ${theme.ty('r-xs', '400', 'r-s')}

        display: flex;
        flex-direction: column;
        > .text {
          color: ${theme.colors.b0};
          > span {
            ${theme.ty('r-s', '400')}
            color: ${theme.colors.b0}
          }
        }
      }
    }
    > .SpecialLabels {
      > .Energylabel {
        left: -0.75rem;
      }
    }
  }
`
